import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Dayjs } from 'dayjs';
import { LATEST } from 'lumen/utils/constants/pi-constant-values';

const MESSAGES = {
  MISSING: 'general.missing',
} as const;

type Message = '' | (typeof MESSAGES)[keyof typeof MESSAGES];

export default class ParityStrategyEvolution extends Model {
  [Type] = 'parity-strategy/evolution' as const;

  @attr('number') declare benchmarkLossScore: number;
  @attr('string') declare extractDate: string;
  @attr('number') declare lossScoreHotels: number;
  @attr('number') declare lossScoreShops: number;
  @attr('string') declare message: Message;
  @attr('number') declare numberOfHotels: number;
  @attr('number') declare numberOfHotelsWithIssues: number;
  @attr('number') declare numberOfShops: number;
  @attr('number') declare numberOfShopsWithIssues: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare psSelections: Services['ps-selections'];

  get extractDateMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(this.extractDate);
  }

  get formattedBenchmarkLossScore(): string {
    return this.intl.formatNumber(this.benchmarkLossScore, {
      style: 'percent',
    });
  }

  get formattedLossScore(): string {
    return this.intl.formatNumber(this.lossScore, { style: 'percent' });
  }

  get isLatest(): boolean {
    return this.extractDate === LATEST;
  }

  get isMissing(): boolean {
    return this.message === MESSAGES.MISSING;
  }

  get lossScore(): number {
    return this.psSelections.isHotelSelected
      ? this.lossScoreShops
      : this.lossScoreHotels;
  }

  get numberOfRecords(): number {
    return this.psSelections.isHotelSelected
      ? this.numberOfShops
      : this.numberOfHotels;
  }

  get numberOfRecordsWithIssues(): number {
    return this.psSelections.isHotelSelected
      ? this.numberOfShopsWithIssues
      : this.numberOfHotelsWithIssues;
  }
}
